import {
  Box,
  Checkbox,
  ListItemText,
  MenuItem,
  Select,
  Typography
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import { CancelIconWrapper, Search, SearchIconWrapper, SearchInputBase } from "../../components/Header/Header.styles";
import { Icon } from "../Icon/Icon.component";

interface ExternalSidebarProps {
  filter: string;
  selectedStatus: string[];
  setSelectedStatus: (value: string[]) => void;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
}

export function ExternalSidebar({
  filter,
  selectedStatus,
  setSelectedStatus,
  searchTerm,
  setSearchTerm
}: ExternalSidebarProps) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [expanded, setExpanded] = React.useState(false);

  // Funzione di utilità per il titolo
  const renderTitle = () => (
    <Typography
      sx={ {
        color: theme.customColors.textPrimary,
        fontSize: "1.5rem",
        fontWeight: 600
      } }
    >
      { filter === "all"
        ? t("all")
        : filter === "iscrizioni"
          ? t("subscriptions")
          : t("consuntivations") }
    </Typography>
  );

  return (
    <Box
      sx={ {
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        width: "100%",
        mb: 3,
        // spazio verticale tra titolo e controllo solo su xs
        rowGap: { xs: 2, sm: 0 }
      } }
    >
      { /* 1) Titolo */ }
      <Box
        sx={ {
          width: { xs: "100%", sm: "auto" }
        } }
      >
        { renderTitle() }
      </Box>

      { /* 2) Contenitore orizzontale di search + select */ }
      <Box
        sx={ {
          display: "flex",
          flexDirection:{ xs: "column", sm: "row" },
          alignItems: "center",
          width: { xs: "100%", sm: "auto" },
          gap: 2
        } }
      >
        { /* Barra di ricerca */ }
        <Box
          display="flex"
          flexGrow={ 1 }
          justifyContent="center"
        >
          <Search
            sx={ {
              maxWidth: "unset",
              width: "100%",
              [theme.breakpoints.down("sm")]: {
                display: "flex",
                padding: "8px 16px"
              }
            } }
          >
            <SearchIconWrapper>
              <Icon
                color={ theme.customColors.systemPrimary02 }
                icon={ "Icons_search" }
                width={ "24px" }
                height={ "24px" }
              />
            </SearchIconWrapper>
            <SearchInputBase
              value={ searchTerm }
              onChange={ e => setSearchTerm(e.target.value) }
              inputProps={ { "aria-label": "search" } }
              placeholder={ `${t("search_training")}?` }
            />
            <CancelIconWrapper
              aria-label={ t("empty_field") }
              aria-hidden={ !searchTerm ? "true" : undefined }
              onClick={ () => setSearchTerm("") }>
              {
                searchTerm ? (
                  <Icon
                    aria-hidden="true"
                    icon={ "close" }
                    width={ "20px" }
                    height={ "20px" }
                    color={ theme.customColors.systemPrimary02 }
                  />
                ) : null
              }
            </CancelIconWrapper>
          </Search>
        </Box>

        { /* Select status */ }
        <Select
          multiple
          onOpen={ () => setExpanded(true) }
          onClose={ () => setExpanded(false) }                                // ⬅️ importante
          value={ selectedStatus }                        // array
          onChange={ (e) => setSelectedStatus(e.target.value as string[]) }

          displayEmpty
          renderValue={ () => t("status") }

          // Sostituiamo l’icona di default con il nostro <Icon> di IcoMoon
          IconComponent={ () => (
            <Box
              sx={ {
                position: "absolute",
                right: 10,
                top: "30%",
                transform: `${expanded ? "translateY(-40%)  rotate(180deg)" : "rotate(0deg)"}`,
                transition: "transform .4s linear",
                pointerEvents: "none"
              } }
            >
              <Icon
                icon="dropdown"
                width="20px"
                height="16px"
                color="#000"
              />
            </Box>
          ) }
          sx={ {
            backgroundColor: `${theme.customColors.backgroundSecondary} !important`,
            borderRadius: "12px",
            border: `1px solid ${theme.customColors.border}`,
            boxShadow: "none",
            color: "#000",
            width: { xs: "100%", sm: "150px" },
            fontWeight: 500,
            textTransform: "capitalize",
            minHeight: "40px",
            ".MuiSelect-select": {
              paddingTop: "10px",
              paddingBottom: "10px"
            }
          } }

          MenuProps={ {
            PaperProps: {
              sx: {
                backgroundColor: "#fff !important",
                color: "#000 !important",
                boxShadow: theme.shadows[3],
                borderRadius: "12px",
                mt: 1
              }
            }
          } }
        >
          <MenuItem value="PENDING" sx={ { px: 2 } }>
            <ListItemText primary={ t("pending") } primaryTypographyProps={ { fontWeight: 600, fontSize: "14px", textTransform: "capitalize" } } />
            <Checkbox checked={ selectedStatus.includes("PENDING") } sx={ { p: 0.5, mr: 1 } } />
          </MenuItem>
          <MenuItem value="APPROVED" sx={ { px: 2 } }>
            <ListItemText primary={ t("approved") } primaryTypographyProps={ { fontWeight: 600, fontSize: "14px", textTransform: "capitalize" } } />
            <Checkbox checked={ selectedStatus.includes("APPROVED") } />
          </MenuItem>
          <MenuItem value="REJECTED" sx={ { px: 2 } }>
            <ListItemText primary={ t("rejected") } primaryTypographyProps={ { fontWeight: 600, fontSize: "14px", textTransform: "capitalize" } } />
            <Checkbox checked={ selectedStatus.includes("REJECTED") } />
          </MenuItem>
        </Select>
      </Box>
    </Box>
  );
}

export default ExternalSidebar;
